import {
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';
import BaseButton from '../components/BaseButton';
import {setAuthenticatedAction} from '../features/User';
import {useDispatch} from 'react-redux';
import _includes from 'lodash/includes';
import {yupResolver} from '@hookform/resolvers/yup/dist/yup';
import {useForm} from 'react-hook-form';
import {SearchOutlined} from '@mui/icons-material';

import TwoColumnsContainer from '../components/TwoColumnsContainer';
import ViewerContainer from '../components/ViewerContainer';
import useWindowDimensions from '../hooks/useWindowDimensions';
import TextFieldController from '../components/TextFieldController';

import UserValidationSchema, {
  getDefaultUserValidationValues,
} from '../schemas/UserValidationSchema';
import PinValidationSchema, {
  getDefaultPinValidationValues,
} from '../schemas/PinValidationSchema';
import FormHelperTextWithLink from '../components/FormHelperTextWithLink';
import {
  useLazyGetDocumentsQuery,
  usePostPinMutation,
} from '../api/DocViewerAPI';
import ErrorMessage from '../components/ErrorMessage';

const steps = ['Ingreso de Datos Personales', 'Validación de PIN'];

const UserDataForm = ({isSubmitting, resultHandler}) => {
  const [error, setError] = useState(null);

  const {
    control,
    getValues,
    setError: setFormError,
    handleSubmit,
    formState: {errors: formErrors, isValid: isFormValid},
  } = useForm({
    mode: 'all', //  onChange | onBlur | onSubmit | onTouched | all
    resolver: yupResolver(UserValidationSchema),
    defaultValues: {...getDefaultUserValidationValues()},
  });

  const [
    postPinQuery,
    {
      data,
      error: queryError,
      // isLoading,
      isSuccess,
    },
  ] = usePostPinMutation();

  useEffect(() => {
    if (isSubmitting) {
      setError(null);

      handleSubmit(onSubmit)().then(() => {
        if (!isFormValid) {
          resultHandler(false);
        }
      });
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (isSuccess) {
      resultHandler({
        ...getValues(),
        ...data,
      });
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (queryError) {
      resultHandler(false);

      console.log(queryError);

      let message = 'Ha ocurrido un error.';

      if (
        queryError.hasOwnProperty('status') &&
        Number.isInteger(queryError.status)
      ) {
        switch (queryError.status) {
          case 400:
            message = 'La solicitud no pudo ser procesada.';

            setError(message);
            break;
          case 404:
            message = 'Usuario no encontrado.';

            setError(message);
            // setFormError('rut', {message: message, type: 'custom'});
            break;
          default:
            setError(message);
        }
      } else {
        setError(message);
      }
    }
  }, [queryError]);

  const onSubmit = () => postPinQuery(getValues());

  return (
    <Stack direction={'column'} spacing={2}>
      <Typography variant={'h6'} fontWeight={'bold'}>
        Ingrese sus Datos Personales
      </Typography>

      {error !== null && (
        <Grid
          container
          sx={{
            // backgroundColor: 'pink',
            pb: 1,
            flex: 1,
          }}>
          <Grid item xs={12} md={5} lg={4}>
            <ErrorMessage
              error={error}
              sx={
                {
                  // backgroundColor: 'pink',
                }
              }
            />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        component={'form'}
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          // backgroundColor: 'pink',
          // height: '100%',
          flex: 1,
        }}>
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            marginRight: {md: 8},
          }}>
          <TextFieldController
            name={'rut'}
            label={'RUT'}
            control={control}
            errors={formErrors}
            required
            // disabled={isBusy}
            autoFocus={true}
            autoComplete="off"
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            marginTop: {xs: 2, md: 0},
          }}>
          <TextFieldController
            name={'email'}
            label={'Correo Electrónico'}
            control={control}
            errors={formErrors}
            required
            // disabled={isBusy}
            autoComplete="off"
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

const PinValidationForm = ({initial_data, isSubmitting, resultHandler}) => {
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(
    initial_data ? initial_data['email'] : null,
  );

  const {
    trigger,
    control,
    getValues,
    handleSubmit,
    setError: setFormError,
    formState: {errors: formErrors, isValid: isFormValid},
  } = useForm({
    mode: 'all', //  onChange | onBlur | onSubmit | onTouched | all
    resolver: yupResolver(PinValidationSchema),
    defaultValues: {...getDefaultPinValidationValues(initial_data)},
  });

  const [
    getDocumentsQuery,
    {error: queryError, data: queryData, isSuccess: isQuerySuccess},
  ] = useLazyGetDocumentsQuery(
    {}, // _dc: 'TODO'
    {refetchOnMountOrArgChange: false},
  );

  const [
    resendPinQuery,
    {
      data: resendResponse,
      error: resendError,
      isLoading: isResendLoading,
      isSuccess: isResendSuccess,
    },
  ] = usePostPinMutation();

  useEffect(() => {
    if (isSubmitting) {
      setError(null);

      if (isResendLoading) {
        resultHandler(false);
        return;
      }

      handleSubmit(onSubmit)().then(() => {
        if (!isFormValid) {
          resultHandler(false);
        }
      });
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (isQuerySuccess) {
      resultHandler(queryData);
    }
  }, [isQuerySuccess, queryData]);

  useEffect(() => {
    if (isResendSuccess) {
      const {email} = getValues();
      setEmail(email);

      resultHandler({...resendResponse, email}, false); // activate_next=false
    }
  }, [isResendSuccess, resendResponse]);

  useEffect(() => {
    if (queryError) {
      resultHandler(false);

      let message = 'Ha ocurrido un error.';

      if (
        queryError.hasOwnProperty('status') &&
        Number.isInteger(queryError.status)
      ) {
        switch (queryError.status) {
          case 400:
            message = 'El PIN no es válido.';

            setFormError('code', {message: message, type: 'custom'});
            break;
          case 422:
            message = 'La solicitud no pudo ser procesada.';

            setError(message);
            break;
          default:
            setError(message);
        }
      } else {
        setError(message);
      }
    }
  }, [queryError]);

  useEffect(() => {
    if (resendError) {
      resultHandler(false);

      let message = 'Ha ocurrido un error.';

      if (
        resendError.hasOwnProperty('status') &&
        Number.isInteger(resendError.status)
      ) {
        switch (resendError.status) {
          case 404:
            message = 'Usuario no encontrado.';

            // setError(message);
            setFormError('email', {message: message, type: 'custom'});
            break;
          default:
            setError(message);
        }
      } else {
        setError(message);
      }
    }
  }, [resendError]);

  const get_params = () => {
    const {rut} = initial_data || {rut: '55.555.555-5'};
    const {code, email} = getValues();
    const {transaction_ref_id} = resendResponse
      ? resendResponse
      : initial_data || {
          transaction_ref_id: 'fa093bde-019f-4289-bf14-08dac0ca92a4',
        };

    // console.log(JSON.stringify(params));

    return {
      transaction_ref_id,
      code,
      rut,
      email,
    };
  };

  const onSubmit = () => {
    const {rut, code, email, transaction_ref_id} = get_params();

    getDocumentsQuery({rut, code, email, transaction_ref_id});
  };

  const onResend = () => {
    trigger('email').then((valid) => {
      if (valid) {
        const {rut, email} = get_params();

        resendPinQuery({rut, email});
      }
    });
  };

  return (
    <Stack direction={'column'} spacing={2}>
      <Typography variant={'h6'} fontWeight={'bold'}>
        Ingrese su PIN de validación{' '}
        {process.env.NODE_ENV === `development` && JSON.stringify(get_params())}
      </Typography>

      <Typography variant={'body1'}>
        Le hemos enviado un PIN de validación al correo electrónico{' '}
        <Link
          component="a"
          variant="inherit"
          href={'mailto:' + email}
          underline={'none'}>
          {email}
        </Link>
        . Busque el PIN que se encuentra en su correo electrónico e ingréselo en
        la casilla que se encuentra debajo.
      </Typography>

      <Typography variant={'body1'} style={{marginTop: 0}}>
        Si escribió mal el correo electrónico o desea actualizarlo, hágalo aquí
        y presione Reenviar Correo.
      </Typography>

      {error !== null && (
        <Grid
          container
          sx={{
            // backgroundColor: 'pink',
            pb: 1,
            flex: 1,
          }}>
          <Grid item xs={12} md={5} lg={4}>
            <ErrorMessage
              error={error}
              sx={
                {
                  // backgroundColor: 'pink',
                }
              }
            />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        component={'form'}
        onSubmit={handleSubmit(onSubmit)}
        pt={1}
        sx={{
          // backgroundColor: 'pink',
          // height: '100%',
          flex: 1,
        }}>
        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            marginRight: {md: 8},
          }}>
          <TextFieldController
            name={'code'}
            label={'PIN de Validación'}
            control={control}
            errors={formErrors}
            required
            // disabled={isBusy}
            autoFocus={true}
            autoComplete="off"
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          lg={4}
          sx={{
            marginTop: {xs: 2, md: 0},
          }}>
          <TextFieldController
            name={'email'}
            label={'Correo Electrónico'}
            control={control}
            errors={formErrors}
            required
            // disabled={isBusy}
            autoComplete="off"
            FormHelperTextProps={{
              component: FormHelperTextWithLink,
              text: 'Reenviar Correo',
              isBusy: isResendLoading,
              onClick: onResend,
              // isBusy: isValidationLoading || isResendLoading,
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

const FormsContainer = ({setDataHandler}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);

  const isAuthenticated = useIsAuthenticated();

  const dispatch = useDispatch();

  useEffect(() => {
    if (activeStep === steps.length) {
      dispatch(setAuthenticatedAction(true));
    }
  }, [activeStep]);

  useEffect(() => {
    if (data && data.hasOwnProperty('documents')) {
      setDataHandler(data['documents']);
    }
  }, [isAuthenticated, data]);

  const onNext = () => setSubmitting(true);

  const onResult = useCallback(
    (result, activate_next = true) => {
      console.log('[container][onResult]:', JSON.stringify({success: result}));

      if (result) {
        if (activate_next) {
          setActiveStep((prevState) => prevState + 1);
        }

        setData((prevState) => ({...prevState, ...result}));
      }

      setSubmitting(false);
    },
    [setActiveStep, setSubmitting],
  );

  const getStepContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return (
          <UserDataForm isSubmitting={isSubmitting} resultHandler={onResult} />
        );
      case 1:
        return (
          <PinValidationForm
            isSubmitting={isSubmitting}
            resultHandler={onResult}
            initial_data={data}
          />
        );
      default:
    }
  }, [activeStep, isSubmitting]);

  return (
    <>
      <Typography
        variant={'subtitle1'}
        fontSize={'20px'}
        marginY={2}
        fontWeight={'bold'}>
        Ver Mis documentos{' '}
        {process.env.NODE_ENV === `development` && JSON.stringify(data)}
      </Typography>

      <Paper variant={'outlined'} sx={{paddingY: 2}}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper variant={'outlined'} sx={{padding: 4}}>
        {getStepContent()}
      </Paper>

      <Paper variant={'outlined'} sx={{paddingX: 4, paddingY: 2}}>
        <Box
          // item
          // xs={isFirstStep() ? 2 : 12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
          <BaseButton
            variant="contained"
            loading={isSubmitting}
            // disabled={!isAccepted || !isConfirmed || isPaymentDisbursed()}
            onClick={onNext}>
            {activeStep === steps.length - 1 ? 'Finalizar' : 'Siguiente'}
          </BaseButton>
        </Box>
      </Paper>
    </>
  );
};

const DataContainer = ({items}) => {
  const {height, width} = useWindowDimensions();
  const [filteredItems, setFilterItems] = useState(items);

  // console.log(JSON.stringify({items}));

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [queryText, setQueryText] = useState('');

  // const onSearch = useCallback(
  //   ({target: {value}}) => {
  //     setQueryText(value ? value.trim().toLowerCase() : '');
  //     setSelectedIndex(0);
  //   },
  //   [setQueryText, setSelectedIndex],
  // );

  const onTextChange = useCallback(
    (event) => {
      const value = event.target.value
        ? event.target.value.trim().toLowerCase()
        : '';

      // console.log(`Query: ${value}`);

      setQueryText(value);

      if (value === '') {
        setFilterItems(items); //reset
        setSelectedIndex(0);
      }
    },
    [setQueryText],
  );

  const onSearch = () => {
    // console.log(queryText);

    setFilterItems(
      items.filter((document) =>
        queryText.length
          ? _includes(document.name.toLowerCase(), queryText)
          : document,
      ),
    );

    // setQueryText(value ? value.trim().toLowerCase() : '');
    setSelectedIndex(0);
  };

  const onSelect = useCallback(
    (index) => setSelectedIndex(index),
    [setSelectedIndex],
  );

  return (
    <Stack
      direction={'column'}
      spacing={0}
      sx={
        {
          // backgroundColor: 'pink',
        }
      }>
      <Typography variant={'subtitle1'} fontSize={'20px'} marginY={2}>
        Mis Documentos ({filteredItems.length})
      </Typography>

      <TwoColumnsContainer
        leftColumn={
          <Stack
            direction={'column'}
            flex={1}
            sx={{
              height: height - 260,
              backgroundColor: 'background.paper',
            }}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              marginY={2}
              marginX={2}
              sx={
                {
                  // backgroundColor: 'pink',
                }
              }
              justifyContent={'space-between'}>
              <Typography fontWeight={'bold'}>Nombre</Typography>
              <TextField
                variant="standard"
                placeholder={'Buscar ...'}
                sx={
                  {
                    // backgroundColor: 'pink'
                  }
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSearch();
                  }
                }}
                onChange={onTextChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="buscar"
                        onClick={onSearch}
                        edge="end">
                        <SearchOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <List
              style={{overflow: 'auto'}}
              sx={{
                flex: 1,
                backgroundColor: 'background.paper',
                padding: 0,
                // borderBottom: '1px solid white',
                // paddingBottom: 0,
              }}>
              {filteredItems.map((document, index, array) => (
                <ListItemButton
                  key={index}
                  selected={selectedIndex === index}
                  sx={{paddingY: 2}}
                  onClick={onSelect.bind(this, index)}>
                  <Typography>{document.name}</Typography>
                </ListItemButton>
              ))}
            </List>
          </Stack>
        }
        rightColumn={
          <ViewerContainer
            url={items.length ? items[selectedIndex]['preview_url'] : null}
            height={height - 300}
          />
        }
        rightColumnSx={{
          backgroundColor: '#e0e0e0',
        }}
        ignoreResponsive={true}
      />
    </Stack>
  );
};

const PrivateViewerPage = () => {
  const [items, setItems] = useState([]);

  return (
    <Container sx={{flex: 1, display: 'flex'}}>
      <Stack
        direction={'column'}
        spacing={0.5}
        flex={1}
        sx={{
          // backgroundColor: 'pink',
          // marginX: 2,
          paddingY: 2,
        }}>
        {items.length ? (
          <DataContainer items={items} />
        ) : (
          <FormsContainer setDataHandler={setItems} />
        )}
      </Stack>
    </Container>
  );
};

export default PrivateViewerPage;
